import { memo } from 'react'
import { Box } from '@mui/material'

export type GhostRowsVariant =
    | 'watchlist'
    | 'orders'
    | 'positions'
    | 'holdings'
    | 'alerts'
    | 'alertLogs'
    | 'news'
    | 'notifications'
    | 'trade'

type Cell =
    | { kind: 'bar'; width: number | string }
    | { kind: 'pill'; width: number }
    | { kind: 'dot' }
    | { kind: 'block' }
    | { kind: 'sparkline'; shape: number }
    | { kind: 'space' }

const bar = (width: number | string): Cell => ({ kind: 'bar', width })
const pill = (width: number): Cell => ({ kind: 'pill', width })
const spark = (shape: number): Cell => ({ kind: 'sparkline', shape })
const dot: Cell = { kind: 'dot' }
const block: Cell = { kind: 'block' }
const space: Cell = { kind: 'space' }

const SPARKLINE_SHAPES = [
    '0,13 10,10 20,12 30,6 40,8 50,4 60,7 70,3',
    '0,5 10,8 20,6 30,11 40,9 50,13 60,10 70,14',
    '0,9 10,7 20,10 30,8 40,5 50,9 60,6 70,8'
]

/**
 * Faint skeleton rows that preview what a populated list looks like.
 * Each preset mirrors the column layout of the real rows in that tab.
 */
const PRESETS: Record<GhostRowsVariant, Cell[][]> = {
    watchlist: [
        [bar(56), spark(0), space, bar(40)],
        [bar(44), spark(1), space, bar(34)],
        [bar(62), spark(2), space, bar(44)]
    ],
    orders: [
        [bar(58), pill(22), space, bar(26), bar(40)],
        [bar(46), pill(22), space, bar(22), bar(36)],
        [bar(64), pill(22), space, bar(30), bar(44)]
    ],
    positions: [
        [bar(58), bar(22), space, bar(44)],
        [bar(46), bar(18), space, bar(36)],
        [bar(64), bar(26), space, bar(40)]
    ],
    holdings: [
        [bar(58), space, bar(28), bar(44)],
        [bar(46), space, bar(24), bar(38)],
        [bar(64), space, bar(30), bar(42)]
    ],
    alerts: [
        [dot, bar(96), space, pill(28)],
        [dot, bar(78), space, pill(28)],
        [dot, bar(108), space, pill(28)]
    ],
    alertLogs: [
        [dot, bar(96), space, bar(30)],
        [dot, bar(78), space, bar(26)],
        [dot, bar(108), space, bar(32)]
    ],
    news: [
        [block, bar('58%'), space, bar(22)],
        [block, bar('46%'), space, bar(22)],
        [block, bar('64%'), space, bar(22)]
    ],
    notifications: [
        [dot, bar(104), space, bar(26)],
        [dot, bar(84), space, bar(26)],
        [dot, bar(116), space, bar(26)]
    ],
    trade: [
        [pill(22), bar(64), space, bar(26), bar(40)],
        [pill(22), bar(52), space, bar(22), bar(36)],
        [pill(22), bar(70), space, bar(30), bar(44)]
    ]
}

const ROW_OPACITY = [0.55, 0.34, 0.16]

const renderCell = (cell: Cell, index: number) => {
    switch (cell.kind) {
        case 'bar':
            return <Box key={index} sx={{ width: cell.width, height: 8, borderRadius: 4, bgcolor: 'currentColor', flexShrink: 0 }} />
        case 'pill':
            return (
                <Box
                    key={index}
                    sx={{ width: cell.width, height: 14, borderRadius: 7, border: '1.5px solid currentColor', boxSizing: 'border-box', flexShrink: 0 }}
                />
            )
        case 'dot':
            return <Box key={index} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'currentColor', flexShrink: 0 }} />
        case 'block':
            return <Box key={index} sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: 'currentColor', flexShrink: 0 }} />
        case 'sparkline':
            return (
                <svg key={index} width="70" height="18" viewBox="0 0 70 18" style={{ flexShrink: 0 }}>
                    <polyline
                        points={SPARKLINE_SHAPES[cell.shape % SPARKLINE_SHAPES.length]}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    />
                </svg>
            )
        case 'space':
            return <Box key={index} sx={{ flex: 1 }} />
    }
}

type Props = {
    variant: GhostRowsVariant
}

const GhostRows = ({ variant }: Props) => (
    <Box aria-hidden sx={{ width: '100%', color: 'text.secondary' }}>
        {PRESETS[variant].map((cells, rowIndex) => (
            <Box
                key={rowIndex}
                sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '9px', opacity: ROW_OPACITY[rowIndex] ?? 0.1 }}
            >
                {cells.map(renderCell)}
            </Box>
        ))}
    </Box>
)

export default memo(GhostRows)
